import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetDepositsQuery } from "@/store/rest_api";
import { Receipt, TrendingUp, TrendingDown, CoinsIcon } from "lucide-react";
import { format } from "date-fns";
import React from "react";

export function TransactionHistory() {
  const { data: deposits, isLoading } = useGetDepositsQuery();

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "completed":
        return "default";
      case "pending":
        return "secondary";
      case "failed":
        return "destructive";
      case "refunded":
        return "outline";
      default:
        return "secondary";
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case "credit_purchase":
        return "Credit Purchase";
      case "subscription_fee":
        return "Subscription Fee";
      case "trial_credit":
        return "Trial Credit";
      case "auto_topup":
        return "Auto Top-up";
      case "refund":
        return "Refund";
      case "sales_tax":
        return "Sales Tax";
      case "admin_credit":
        return "Credit from Retreevo";
      case "referral_credit":
        return "Referral credit";
      default:
        return type;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "credit_purchase":
      case "trial_credit":
      case "auto_topup":
      case "refund":
      case "admin_credit":
      case "referral_credit":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "subscription_fee":
      case "sales_tax":
        return <TrendingDown className="h-4 w-4 text-orange-500" />;
      default:
        return <Receipt className="h-4 w-4 text-muted-foreground" />;
    }
  };

  if (isLoading) {
    return (
      <div className="container p-4">
        <Card>
          <CardHeader>
            <CardTitle>Transaction History</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container p-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CoinsIcon className="h-5 w-5" />
            Transaction History
          </CardTitle>
          <CardDescription>View all your account transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {!deposits || deposits.length === 0 ? (
            <div className="text-center py-12">
              <Receipt className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No transactions yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {deposits.map((deposit) => (
                <div
                  key={deposit.id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1">{getTypeIcon(deposit.type)}</div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{deposit.description || getTypeLabel(deposit.type)}</p>
                        <Badge variant={getStatusVariant(deposit.status)} className="text-xs">
                          {deposit.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(deposit.createdAt), "MMM dd, yyyy HH:mm")}
                      </p>
                      {deposit.documentNumber && deposit.status === "completed" && (
                        <p className="text-xs text-muted-foreground mt-1">Receipt {deposit.documentNumber}</p>
                      )}
                      {deposit.stripePaymentIntentId && (
                        <p className="text-xs text-muted-foreground mt-1">ID: {deposit.stripePaymentIntentId}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold">${deposit.amount.toFixed(2)}</p>
                    {deposit.completedAt && (
                      <p className="text-xs text-muted-foreground">
                        Completed {format(new Date(deposit.completedAt), "MMM dd")}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
